import React from 'react'
import {
  get_commento_list, get_risposte_numeriche,
  get_feed_modifiche_commenti, get_feed_modifiche_problemi, get_feed_assegnazione,
  get_feed_assegnazione_evento,
  get_commenti_shortlist,
  get_citazioni_thread,
  get_commento_shortlist_detail
} from "../api"

import {
  CommentoFeed, RispostaFeed, ThreadFeed, ModificaProblemaFeed,
  CommentoVersioneFeed, AssegnazioneFeed, AssegnazioneEventoFeed, Commento, CitazioneThreadFeed
} from '../components';
import { useThread } from '../reducers';


interface FeedFilter {
  data__lte?: string,
  data__gte?: string,
  problema?: number,
  commento__thread?: number,
  thread?: number,
  thread__pk?: number,
  partecipante?: number,
  autore?: number,
}


interface Filter {
  commenti: FeedFilter,
  commenti_shortlist: FeedFilter,
  risposte: FeedFilter,
  assegnazione: FeedFilter,
  modifiche_commenti: FeedFilter,
  modifiche_problemi: FeedFilter,
  citazioni: FeedFilter,
}

type ProbArr = ReturnType<typeof useThread>

type FeedEntry = {
  date: string | Date;
  content: React.ReactNode;
  isComment?: boolean;
};


export const emptyFeed: Awaited<ReturnType<typeof fetchFeed>> = {
  commenti_thread: [],
  commenti_shortlist: [],
  risposte: [],
  assegnazione_problemi: [],
  assegnazione_evento: [],
  modifiche_problemi: [],
  modifiche_comm: [],
  citazioni_thread: [],
}

type V<T extends (...args: any[]) => any> = Awaited<ReturnType<T>>;


interface Feed {
  commenti_thread: V<typeof get_commento_list>,
  commenti_shortlist: V<typeof get_commenti_shortlist>,
  risposte: V<typeof get_risposte_numeriche>,
  assegnazione_problemi: V<typeof get_feed_assegnazione>,
  assegnazione_evento: V<typeof get_feed_assegnazione_evento>,
  modifiche_problemi: V<typeof get_feed_modifiche_problemi>,
  modifiche_comm: V<typeof get_feed_modifiche_commenti>,
  citazioni_thread: V<typeof get_citazioni_thread>,
}

export const fetchFeed: (filtro: Filter) => Promise<Feed> = async (filtro) => {
  const feed = {
    commenti_thread: get_commento_list(filtro.commenti),
    commenti_shortlist: get_commenti_shortlist(filtro.commenti),
    risposte: get_risposte_numeriche(filtro.risposte),
    assegnazione_problemi: get_feed_assegnazione(filtro.assegnazione),
    assegnazione_evento: get_feed_assegnazione_evento(filtro.assegnazione),
    modifiche_problemi: get_feed_modifiche_problemi(filtro.modifiche_problemi),
    modifiche_comm: get_feed_modifiche_commenti(filtro.modifiche_commenti),
    citazioni_thread: get_citazioni_thread(filtro.citazioni),
  }
  const entries = Object.entries(feed);

  const results = await Promise.allSettled(entries.map(([k, p]) => p));
  const awaited_feed = results.reduce((acc, result, i) => {
    const key = entries[i][0] as keyof Feed;
    // @ts-ignore
    acc[key] = result.status === "fulfilled" ? result.value : [];
    if (result.status !== "fulfilled") {
      console.error("Failed to do request", result);
    }
    return acc;
  }, {} as Feed);

  return awaited_feed;
}

export const renderFeed = (
  feed: Awaited<ReturnType<typeof fetchFeed>>,
  problemi: ProbArr,
  filterProb: (prob: ProbArr[0]) => boolean,
  detail_commento: boolean,
  show_link: boolean) => {


  const CommentoComponent = detail_commento ? Commento : CommentoFeed

  const commenti = feed.commenti_thread.concat(feed.commenti_shortlist);
  // Filtra via commenti shortlist
  const feed_comm: FeedEntry[] = commenti.concat().map(item => {
    return {
      date: item.data, content: <CommentoComponent commento={item} />, isComment: true,
    };
  });
  const feed_risp = feed.risposte.map(item => {
    return {
      date: item.data,
      content: <RispostaFeed risposta={item} link={show_link} />
    };
  });
  const feed_thread = problemi.filter(filterProb)
    .map(item => {
      return {
        date: item.data_inizio,
        content: <ThreadFeed thread={item} />
      }
    })
  const feed_mod_comm = feed.modifiche_comm.filter(item => (item.num != 0))
    .map(item => {
      return {
        date: item.data,
        content: <CommentoVersioneFeed commento_versione={item} problemi={problemi} />
      };
    });
  const feed_mod_problemi = feed.modifiche_problemi.filter(item => (item.num != 0))
    .map(item => {
      return {
        date: item.data,
        content: <ModificaProblemaFeed versione={item} link={show_link} problemi={problemi} />
      }
    })
  const feed_assegnazioni = feed.assegnazione_problemi.map(item => {
    return {
      date: item.data,
      content: <AssegnazioneFeed assegnazione={item} />
    }
  });
  const feed_assegnazioni_evento = feed.assegnazione_evento.map(item => {
    return {
      date: item.data,
      content: <AssegnazioneEventoFeed assegnazione={item} />
    }
  });
  const feed_citazioni_thread = feed.citazioni_thread.map(item => {
    return {
      date: item.data,
      content: <CitazioneThreadFeed citazione={item} />
    }
  })

  return feed_comm
    .concat(feed_risp)
    .concat(feed_thread)
    .concat(feed_mod_comm)
    .concat(feed_mod_problemi)
    .concat(feed_assegnazioni)
    .concat(feed_assegnazioni_evento)
    .concat(feed_citazioni_thread)
    .sort((a, b) => {
      return (new Date(a.date)).getTime() - (new Date(b.date)).getTime();
    }).map((item, idx) => detail_commento ? (
      <div key={idx} className={item.isComment ? 'mb-3' : 'small text-muted border-left px-3 py-2 mb-2'}>
        {item.content}
      </div>
    ) : <React.Fragment key={idx}>{item.content}</React.Fragment>);
}
